// Clasifica un viaje/chat por país y ruta con nombre, replicando la lógica del
// backend (api/_utils/lobby/checkRoute.js + isWithinMargin.js). La definición de
// rutas (coordenadas + margen) viene de GET /admin/routes, no se hardcodea acá.
//
// Convención de coordenadas: [lat, lng] (igual que lobby.Journey.*.coordinates).

const haversineDistance = (coord1, coord2) => {
  const toRad = deg => (deg * Math.PI) / 180
  const lat1 = toRad(coord1.lat)
  const lon1 = toRad(coord1.lng)
  const lat2 = toRad(coord2.lat)
  const lon2 = toRad(coord2.lng)

  const R = 6371 // Radio de la Tierra en km
  const dLat = lat2 - lat1
  const dLon = lon2 - lon1
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

const isWithinMargin = (location, coord, margin) => haversineDistance(location, coord) <= margin

const toLatLng = coord => {
  if (!Array.isArray(coord) || coord.length < 2) return null
  const lat = parseFloat(coord[0])
  const lng = parseFloat(coord[1])
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

// Opciones de UI del filtro. Uruguay queda a nivel país (sin sub-ruta El Este).
export const ROUTE_FILTER_OPTIONS = [
  { value: 'all', label: 'Todas las rutas' },
  { value: 'uy', label: 'Uruguay' },
  { value: 'ar', label: 'Argentina' },
  { value: 'la-costa', label: 'La Costa' },
  { value: 'rosario', label: 'Rosario' },
  { value: 'costa-rosario', label: 'Costa-Rosario' },
  { value: 'sde-tuc', label: 'SDE-Tucumán' },
  { value: 'salta-jujuy', label: 'Salta-Jujuy' },
  { value: 'salta-tuc', label: 'Salta-Tucumán' },
  { value: 'jujuy-tuc', label: 'Jujuy-Tucumán' },
  { value: 'rc-cba', label: 'Río Cuarto-Córdoba' },
  { value: 'mza-sj', label: 'Mendoza-San Juan' },
  { value: 'caba-tandil', label: 'CABA-Tandil' },
  { value: 'santa-fe-rosario', label: 'Santa Fe-Rosario' },
  { value: 'uy-ar', label: 'Uruguay ↔ Argentina' }
]

const COUNTRY_CODES = ['uy', 'ar']

/**
 * @param {Object} input
 * @param {number[]} [input.origin]       coordenadas origen [lat, lng]
 * @param {number[]} [input.destination]  coordenadas destino [lat, lng]
 * @param {string}   [input.originCountryCode]
 * @param {string}   [input.destinationCountryCode]
 * @param {string}   [input.country]      fallback 'uy' | 'ar' (ej: channel.country)
 * @param {Object}   routesConfig         { margin, argentina: [{ name, locations }] }
 * @returns {{ country: ('uy'|'ar'|null), route: (string|null) }}
 */
export const classifyRoute = (input = {}, routesConfig = {}) => {
  const oc = (input.originCountryCode || '').toLowerCase()
  const dc = (input.destinationCountryCode || '').toLowerCase()

  let country = input.country ? input.country.toLowerCase() : null
  if (COUNTRY_CODES.includes(oc) && oc === dc) country = oc

  let route = null

  const crossBorder =
    COUNTRY_CODES.includes(oc) && COUNTRY_CODES.includes(dc) && oc !== dc
  if (crossBorder) {
    route = 'uy-ar'
  } else {
    const origin = toLatLng(input.origin)
    const destination = toLatLng(input.destination)
    if (origin && destination && Array.isArray(routesConfig.argentina)) {
      const margin = routesConfig.margin ?? 30
      const match = routesConfig.argentina.find(r => {
        const routeMargin = r.margin ?? margin
        return (
          r.locations.some(loc => isWithinMargin(loc, origin, routeMargin)) &&
          r.locations.some(loc => isWithinMargin(loc, destination, routeMargin))
        )
      })
      route = match ? match.name : null
    }
  }

  return { country, route }
}

/**
 * ¿Una clasificación pasa el filtro seleccionado?
 * 'all' -> todo; 'uy'/'ar' -> por país; cualquier otro -> por ruta con nombre.
 */
export const matchesRouteFilter = (classification, filterValue) => {
  if (!filterValue || filterValue === 'all') return true
  if (filterValue === 'uy' || filterValue === 'ar') return classification.country === filterValue
  return classification.route === filterValue
}
